import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ModalBasic from "./ModalBasic.jsx";
import CanView from "./CanView.jsx";

function LocationSelectorModal({ modalOpen, setModalOpen, onLocationSelect }) {
  const dispatch = useDispatch();
  const { selectedMerchant, selectedLocation } = useSelector((state) => state.app);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);

  // Get locations from selected merchant
  const locations = selectedMerchant?.locations || [];

  // Filter and sort locations based on search term (memoized for performance)
  const filteredLocations = useMemo(() => {
    return locations
      .filter((location) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
          location.store_id?.toLowerCase().includes(term) ||
          location.name?.toLowerCase().includes(term) ||
          location.subname?.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        const nameA = (a.subname || a.name || '').toLowerCase();
        const nameB = (b.subname || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [locations, searchTerm]);

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  // Focus search input when modal opens
  useEffect(() => {
    if (modalOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [modalOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!modalOpen) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredLocations.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredLocations.length - 1
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filteredLocations[selectedIndex]) {
          handleLocationSelect(filteredLocations[selectedIndex]);
        }
      } else if (e.key === "Escape") {
        setModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, selectedIndex, filteredLocations]);

  const handleLocationSelect = (location) => {
    dispatch({
      type: "SET_SELECTED_LOCATION",
      payload: location.id,
    });
    
    if (onLocationSelect) {
      onLocationSelect(location);
    }
    
    setModalOpen(false);
    setSearchTerm("");
    setSelectedIndex(0);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSearchTerm("");
    setSelectedIndex(0);
  };

  if (!selectedMerchant) return null;

  return (
    <ModalBasic
      title={
        <div className="flex items-center">
          Select Location
        </div>
      }
      modalOpen={modalOpen}
      setModalOpen={handleClose}
      width="md"
    >
      <div className="space-y-4">
        {/* Merchant Info */}
        <div className="text-sm text-slate-600 dark:text-slate-400">
          <span className="font-medium">Account:</span> {selectedMerchant.name}
        </div>

        {/* Search Input */}
        <div>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by location name or store ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
          />
        </div>

        {/* Results List */}
        <div className="max-h-96 overflow-y-auto border border-slate-200 dark:border-slate-600 rounded-md">
          {filteredLocations.length === 0 ? (
            <div className="p-4 text-center text-slate-500 dark:text-slate-400">
              No locations found
            </div>
          ) : (
            filteredLocations.map((location, index) => (
              <div
                key={location.id}
                className={`p-3 cursor-pointer border-b border-slate-100 dark:border-slate-700 last:border-b-0 ${
                  index === selectedIndex
                    ? "bg-blue-50 dark:bg-blue-900/20"
                    : "hover:bg-slate-50 dark:hover:bg-slate-700/50"
                } ${
                  location.id === selectedLocation?.id
                    ? "bg-green-50 dark:bg-green-900/20 border-l-4 border-l-green-500"
                    : ""
                }`}
                onClick={() => handleLocationSelect(location)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-slate-900 dark:text-slate-100">
                      {location.subname || location.name}
                    </div>
                    {location.store_id && (
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        Store ID: {location.store_id}
                      </div>
                    )}
                  </div>
                  {location.id === selectedLocation?.id && (
                    <div className="text-green-600 dark:text-green-400 text-sm font-medium">
                      Current
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

<CanView webOnly>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Use ↑↓ arrows to navigate, Enter to select, or click to select a location.
        </div>
        </CanView>
      </div>
    </ModalBasic>
  );
}

export default LocationSelectorModal;
